import {
    useCallback,
    useContext,
    useMemo,
    useRef,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import useWebSocket from './use-websocket';

import AuthContext from '@/contexts/auth';
import { getTokens } from '@/lib/utils';

interface WsQuotationMessage {
    event?: string;
    quotation?: {
        slug?: string;
        requester_business_name?: string;
        requester_username?: string;
        supplier_business_name?: string;
        supplier_username?: string;
        item_count?: number;
    };
}

function buildWsUrl(): string | null {
    const { accessToken } = getTokens();
    if (!accessToken) return null;

    const serverUrl = import.meta.env
        .VITE_APP_SERVER_URL as string;
    if (!serverUrl) return null;

    const wsBase = serverUrl
        .replace(/^https:\/\//, 'wss://')
        .replace(/^http:\/\//, 'ws://')
        .replace(/\/api\/?$/, '');

    return `${wsBase}/ws/notifications/?token=${accessToken}`;
}

export default function useQuotationNotifications() {
    const queryClient = useQueryClient();
    const { state } = useContext(AuthContext);

    const handledRef = useRef<Map<string, number>>(
        new Map(),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when auth changes
    const wsUrl = useMemo(() => buildWsUrl(), [state]);

    const handleMessage = useCallback(
        (raw: unknown) => {
            const msg = raw as WsQuotationMessage;
            const q = msg?.quotation;

            if (!msg?.event || !q) return;

            // Only handle quotation events
            if (
                msg.event !== 'quotation_requested'
                && msg.event !== 'quotation_responded'
            ) {
                return;
            }

            // Deduplicate
            const dedupeKey = `${msg.event}:${q.slug ?? ''}`;
            const now = Date.now();
            const last = handledRef.current.get(dedupeKey);
            if (last && now - last < 5000) return;
            handledRef.current.set(dedupeKey, now);

            // Clean old entries
            Array.from(handledRef.current.entries())
                .filter(([, ts]) => now - ts > 10000)
                .forEach(([key]) => {
                    handledRef.current.delete(key);
                });

            if (msg.event === 'quotation_requested') {
                const name = q.requester_business_name
                    || q.requester_username
                    || 'Someone';
                toast.info(
                    `${name} sent you a quotation request`,
                );
            }

            if (msg.event === 'quotation_responded') {
                const name = q.supplier_business_name
                    || q.supplier_username
                    || 'Someone';
                toast.success(
                    `${name} responded to your quotation`,
                );
            }

            // Refresh quotation lists and navbar notifications
            queryClient.invalidateQueries({
                queryKey: ['quotations'],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['notifications-unread-count'],
            });
        },
        [queryClient],
    );

    return useWebSocket({
        url: wsUrl || '',
        onMessage: handleMessage,
        enabled: !!wsUrl,
    });
}
